import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { PlusOutlined } from '@ant-design/icons';
import {
  Alert, Button, Checkbox, Input, Select, Spin,
} from 'antd';
import DatePicker from '../DatePicker';
import EhrField from '../ehr/EhrField';
import { hasPresentValue, parsePrefillDate } from '../../util/ehr';
import cancelledRequest from '../../utils/cancelledRequest';
import csrfHeaders from '../../utils/csrfHeaders';
import './athenaInsuranceSection.scss';

const SELF_RELATIONSHIP_CODE = '1';
const RELATIONSHIP_OPTIONS = [{ value: SELF_RELATIONSHIP_CODE, label: 'Self' }, { value: '2', label: 'Spouse' }, { value: '3', label: 'Child' }, { value: '4', label: 'Other' }];
const trimmed = (value) => String(value ?? '').trim();
const getDrawerPopupContainer = () => document.body;
const initialValues = (prefill) => ({
  planName: trimmed(prefill.planName), memberId: trimmed(prefill.memberId), groupNumber: trimmed(prefill.groupNumber), effectiveDate: parsePrefillDate(prefill.effectiveDate), relationshipCode: SELF_RELATIONSHIP_CODE, firstName: '', lastName: '', dob: null, sex: '', updateAppointments: false,
});

const coverageValidationError = (selectedPackage, values) => {
  if (!selectedPackage) return 'Select an insurance package.';
  if (!hasPresentValue(values.memberId)) return 'Member ID is required.';
  if (!hasPresentValue(values.relationshipCode)) return 'Relationship to insured is required.';
  if (values.relationshipCode === SELF_RELATIONSHIP_CODE) return null;
  return !hasPresentValue(values.firstName) || !hasPresentValue(values.lastName) || !values.dob || !hasPresentValue(values.sex) ? 'Complete the policyholder details.' : null;
};

const AthenaInsuranceCoverage = ({
  credentialId, departmentId, initialPrefill, loadingPackages, onAnalytics, onChange, onValidationChange, sequenceNumber, title, topPackages,
}) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [values, setValues] = useState(() => initialValues(initialPrefill));
  const searchRequestRef = useRef(null);
  const previousDepartment = useRef(departmentId);
  const policyholderSelf = values.relationshipCode === SELF_RELATIONSHIP_CODE;
  const updateValues = useCallback((updates) => setValues((current) => ({ ...current, ...updates })), []);

  useEffect(() => () => searchRequestRef.current?.abort(), []);
  useEffect(() => {
    if (previousDepartment.current === departmentId) return;
    previousDepartment.current = departmentId;
    setCandidates([]);
    setError(null);
  }, [departmentId]);

  const validationError = useMemo(() => coverageValidationError(selectedPackage, values), [selectedPackage, values]);
  const coverage = useMemo(() => {
    if (validationError) return null;
    return {
      sequence_number: sequenceNumber,
      package_id: selectedPackage.value,
      package_name: selectedPackage.label,
      member_id: trimmed(values.memberId),
      group_number: trimmed(values.groupNumber) || undefined,
      effective_date: values.effectiveDate ? values.effectiveDate.format('YYYY-MM-DD') : undefined,
      relationship_code: values.relationshipCode,
      policyholder: policyholderSelf ? { self: true } : {
        self: false, first_name: trimmed(values.firstName), last_name: trimmed(values.lastName), dob: values.dob.format('YYYY-MM-DD'), sex: values.sex,
      },
      update_appointments: values.updateAppointments,
    };
  }, [policyholderSelf, selectedPackage, sequenceNumber, validationError, values]);
  useEffect(() => { onValidationChange(validationError); onChange(coverage); }, [coverage, onChange, onValidationChange, validationError]);

  const packageOptions = useMemo(() => {
    if (!selectedPackage) return topPackages;
    return topPackages.some((item) => String(item.value) === String(selectedPackage.value)) ? topPackages : [selectedPackage, ...topPackages];
  }, [selectedPackage, topPackages]);
  const choosePackage = useCallback((insurancePackage) => {
    setSelectedPackage(insurancePackage || null); setCandidates([]); setError(null);
    if (insurancePackage) onAnalytics?.('athena_insurance_package_selected', { coverage: sequenceNumber, source: 'insurance_section' });
    else onAnalytics?.('athena_insurance_package_cleared', { coverage: sequenceNumber });
  }, [onAnalytics, sequenceNumber]);
  const search = useCallback(async () => {
    if (!credentialId || !hasPresentValue(values.planName) || !hasPresentValue(values.memberId)) return;
    searchRequestRef.current?.abort();
    const controller = new AbortController(); searchRequestRef.current = controller;
    setSearching(true); setError(null); setCandidates([]);
    try {
      const response = await axios.post('/api/v1/emr_insurance_packages/search', { emr_integration_credential_id: credentialId, insurance_plan_name: trimmed(values.planName), member_id: trimmed(values.memberId) }, { headers: csrfHeaders(), signal: controller.signal });
      if (controller.signal.aborted) return;
      const packages = response.data.packages || [];
      setCandidates(packages);
      onAnalytics?.('athena_insurance_package_search_used', { coverage: sequenceNumber, result_count: packages.length });
    } catch (requestError) {
      if (cancelledRequest(requestError, controller)) return;
      setError(requestError?.response?.data?.error || 'Unable to search insurance packages.');
      onAnalytics?.('athena_insurance_package_search_failed', { coverage: sequenceNumber, stage: 'search' });
    } finally { if (!controller.signal.aborted) setSearching(false); }
  }, [credentialId, onAnalytics, sequenceNumber, values.memberId, values.planName]);
  const idPrefix = `athena-insurance-${sequenceNumber}`;

  return (
    <section className="athena-insurance-section__coverage" aria-label={title}>
      {error && <Alert type="warning" showIcon message={error} />}
      {loadingPackages ? <Spin tip="Loading common insurance packages..." /> : <EhrField controlId={`${idPrefix}-package`} label="Common package"><Select id={`${idPrefix}-package`} value={selectedPackage?.value} allowClear placeholder="Select a common package" onChange={(packageId) => choosePackage(packageOptions.find((item) => String(item.value) === String(packageId)))} getPopupContainer={getDrawerPopupContainer} dropdownClassName="ehr-select-dropdown">{packageOptions.map((item) => <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>)}</Select></EhrField>}
      {selectedPackage && (
      <div className="athena-insurance-section__selected">
        Selected package:
        {selectedPackage.label}
        {' '}
        <Button type="link" onClick={() => choosePackage(null)}>Change package</Button>
      </div>
      )}
      <p className="athena-insurance-section__search-help">If your plan is not listed above, enter part of the payer or plan name and member ID, then search and select a package.</p>
      <div className="athena-insurance-section__grid">
        <EhrField controlId={`${idPrefix}-plan`} label="Search payer / plan"><Input id={`${idPrefix}-plan`} value={values.planName} onChange={(event) => updateValues({ planName: event.target.value })} /></EhrField>
        <EhrField controlId={`${idPrefix}-member`} label="Member ID" required><Input id={`${idPrefix}-member`} aria-required="true" value={values.memberId} onChange={(event) => updateValues({ memberId: event.target.value })} /></EhrField>
      </div>
      <Button onClick={search} loading={searching} disabled={!hasPresentValue(values.planName) || !hasPresentValue(values.memberId)}>Search packages</Button>
      {candidates.length > 0 && <div className="athena-insurance-section__candidates" role="listbox" aria-label={`${title} package results`}>{candidates.map((item) => <Button key={item.value} type="default" role="option" aria-selected={String(selectedPackage?.value) === String(item.value)} onClick={() => choosePackage(item)}>{item.label}</Button>)}</div>}
      <div className="athena-insurance-section__grid">
        <EhrField controlId={`${idPrefix}-group`} label="Group number"><Input id={`${idPrefix}-group`} value={values.groupNumber} onChange={(event) => updateValues({ groupNumber: event.target.value })} /></EhrField>
        <EhrField controlId={`${idPrefix}-effective`} label="Effective date"><DatePicker id={`${idPrefix}-effective`} value={values.effectiveDate} onChange={(value) => updateValues({ effectiveDate: value })} getPopupContainer={getDrawerPopupContainer} dropdownClassName="ehr-select-dropdown" /></EhrField>
        <EhrField controlId={`${idPrefix}-relationship`} label="Relationship to insured" required><Select id={`${idPrefix}-relationship`} aria-required="true" value={values.relationshipCode} onChange={(value) => updateValues({ relationshipCode: value })} getPopupContainer={getDrawerPopupContainer} dropdownClassName="ehr-select-dropdown">{RELATIONSHIP_OPTIONS.map((item) => <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>)}</Select></EhrField>
      </div>
      {!policyholderSelf && (
      <div className="athena-insurance-section__grid">
        <EhrField controlId={`${idPrefix}-first`} label="Policyholder first name" required><Input id={`${idPrefix}-first`} aria-required="true" value={values.firstName} onChange={(event) => updateValues({ firstName: event.target.value })} /></EhrField>
        <EhrField controlId={`${idPrefix}-last`} label="Policyholder last name" required><Input id={`${idPrefix}-last`} aria-required="true" value={values.lastName} onChange={(event) => updateValues({ lastName: event.target.value })} /></EhrField>
        <EhrField controlId={`${idPrefix}-dob`} label="Policyholder date of birth" required><DatePicker id={`${idPrefix}-dob`} aria-required="true" value={values.dob} onChange={(value) => updateValues({ dob: value })} getPopupContainer={getDrawerPopupContainer} dropdownClassName="ehr-select-dropdown" /></EhrField>
        <EhrField controlId={`${idPrefix}-sex`} label="Policyholder sex" required>
          <Select id={`${idPrefix}-sex`} aria-required="true" value={values.sex || undefined} placeholder="Select sex" onChange={(value) => updateValues({ sex: value })} getPopupContainer={getDrawerPopupContainer} dropdownClassName="ehr-select-dropdown">
            <Select.Option value="male">Male</Select.Option>
            <Select.Option value="female">Female</Select.Option>
          </Select>
        </EhrField>
      </div>
      )}
      <Checkbox className="athena-insurance-section__checkbox" checked={values.updateAppointments} onChange={(event) => updateValues({ updateAppointments: event.target.checked })}>Update future appointments</Checkbox>
    </section>
  );
};
AthenaInsuranceCoverage.propTypes = {
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), departmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), initialPrefill: PropTypes.object.isRequired, loadingPackages: PropTypes.bool.isRequired, onAnalytics: PropTypes.func, onChange: PropTypes.func.isRequired, onValidationChange: PropTypes.func.isRequired, sequenceNumber: PropTypes.number.isRequired, title: PropTypes.string.isRequired, topPackages: PropTypes.array.isRequired,
};
AthenaInsuranceCoverage.defaultProps = { credentialId: null, departmentId: null, onAnalytics: null };

const AthenaInsuranceSection = ({
  credentialId, departmentId, onAnalytics, onChange, onValidationChange, prefill,
}) => {
  const initiallyExpanded = hasPresentValue(prefill.primary_plan) || hasPresentValue(prefill.primary_member_id) || hasPresentValue(prefill.secondary_plan) || hasPresentValue(prefill.secondary_member_id);
  const initiallySecondary = hasPresentValue(prefill.secondary_plan) || hasPresentValue(prefill.secondary_member_id);
  const [expanded, setExpanded] = useState(initiallyExpanded);
  const [secondaryExpanded, setSecondaryExpanded] = useState(initiallySecondary);
  const [topPackages, setTopPackages] = useState([]); const [loadingPackages, setLoadingPackages] = useState(false); const [loadError, setLoadError] = useState(null);
  const [primaryCoverage, setPrimaryCoverage] = useState(null); const [secondaryCoverage, setSecondaryCoverage] = useState(null);
  const [primaryValidationError, setPrimaryValidationError] = useState(initiallyExpanded ? 'Select an insurance package.' : null);
  const [secondaryValidationError, setSecondaryValidationError] = useState(initiallySecondary ? 'Select an insurance package.' : null);
  const topPackagesRequestRef = useRef(null);
  const onChangeRef = useRef(onChange);
  const onValidationChangeRef = useRef(onValidationChange);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { onValidationChangeRef.current = onValidationChange; }, [onValidationChange]);
  useEffect(() => {
    topPackagesRequestRef.current?.abort();
    if (!expanded || !credentialId) { setLoadingPackages(false); return undefined; }
    const controller = new AbortController(); topPackagesRequestRef.current = controller; setLoadingPackages(true); setLoadError(null);
    axios.get('/api/v1/emr_insurance_packages', { params: { emr_integration_credential_id: credentialId, department_id: departmentId || undefined }, signal: controller.signal }).then((response) => { if (!controller.signal.aborted) setTopPackages(response.data.packages || []); }).catch((requestError) => { if (cancelledRequest(requestError, controller)) return; setLoadError('Unable to load common insurance packages. You can still search by payer and member ID.'); onAnalytics?.('athena_insurance_packages_load_failed', { stage: 'top_packages' }); }).finally(() => { if (!controller.signal.aborted) setLoadingPackages(false); });
    return () => controller.abort();
  }, [credentialId, departmentId, expanded, onAnalytics]);
  const validationError = !expanded ? null : primaryValidationError || (secondaryExpanded && secondaryValidationError ? `Secondary insurance: ${secondaryValidationError}` : null);
  useEffect(() => {
    onValidationChangeRef.current?.(validationError);
    if (!expanded || validationError) {
      onChangeRef.current?.(undefined);
      return;
    }
    onChangeRef.current?.({ coverages: [primaryCoverage, ...(secondaryExpanded ? [secondaryCoverage] : [])] });
  }, [expanded, primaryCoverage, secondaryCoverage, secondaryExpanded, validationError]);
  const add = () => { setExpanded(true); setPrimaryValidationError('Select an insurance package.'); onAnalytics?.('athena_insurance_expanded', { source: 'manual' }); };
  const remove = () => { setExpanded(false); setSecondaryExpanded(false); setPrimaryCoverage(null); setSecondaryCoverage(null); setPrimaryValidationError(null); setSecondaryValidationError(null); onAnalytics?.('athena_insurance_removed', {}); };
  const addSecondary = () => { setSecondaryExpanded(true); setSecondaryValidationError('Select an insurance package.'); onAnalytics?.('athena_secondary_insurance_added', {}); };
  const removeSecondary = () => { setSecondaryExpanded(false); setSecondaryCoverage(null); setSecondaryValidationError(null); onAnalytics?.('athena_secondary_insurance_removed', {}); };
  if (!expanded) {
    return (
      <div className="athena-insurance-section">
        <p className="athena-insurance-section__intro">Optional: add primary and secondary insurance to the new patient chart.</p>
        <Button className="athena-insurance-section__add-button" icon={<PlusOutlined />} onClick={add}>Add insurance</Button>
      </div>
    );
  }
  return (
    <div className="athena-insurance-section" data-testid="athena-insurance-section">
      <div className="athena-insurance-section__header">
        <strong>Insurance</strong>
        <Button type="link" onClick={remove}>Remove</Button>
      </div>
      <p className="athena-insurance-section__help">Select a primary package and member ID. Add secondary insurance if applicable; enter policyholder details when the insured is someone else.</p>
      {loadError && <Alert type="warning" showIcon message={loadError} />}
      {validationError && <Alert type="error" showIcon message={validationError} />}
      <h3 className="athena-insurance-section__coverage-title">Primary insurance</h3>
      <AthenaInsuranceCoverage
        credentialId={credentialId}
        departmentId={departmentId}
        initialPrefill={{
          planName: prefill.primary_plan, memberId: prefill.primary_member_id, groupNumber: prefill.insurance_group_number, effectiveDate: prefill.insurance_effective_date,
        }}
        loadingPackages={loadingPackages}
        onAnalytics={onAnalytics}
        onChange={setPrimaryCoverage}
        onValidationChange={setPrimaryValidationError}
        sequenceNumber={1}
        title="Primary insurance"
        topPackages={topPackages}
      />
      {secondaryExpanded ? (
        <>
          <div className="athena-insurance-section__secondary-header">
            <h3 className="athena-insurance-section__coverage-title">Secondary insurance</h3>
            <Button type="link" onClick={removeSecondary}>Remove secondary</Button>
          </div>
          <AthenaInsuranceCoverage credentialId={credentialId} departmentId={departmentId} initialPrefill={{ planName: prefill.secondary_plan, memberId: prefill.secondary_member_id, groupNumber: prefill.secondary_group_number }} loadingPackages={loadingPackages} onAnalytics={onAnalytics} onChange={setSecondaryCoverage} onValidationChange={setSecondaryValidationError} sequenceNumber={2} title="Secondary insurance" topPackages={topPackages} />
        </>
      ) : <Button className="athena-insurance-section__add-button" icon={<PlusOutlined />} onClick={addSecondary}>Add secondary insurance</Button>}
    </div>
  );
};
AthenaInsuranceSection.propTypes = {
  credentialId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), departmentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]), onAnalytics: PropTypes.func, onChange: PropTypes.func, onValidationChange: PropTypes.func, prefill: PropTypes.object,
};
AthenaInsuranceSection.defaultProps = {
  credentialId: null, departmentId: null, onAnalytics: null, onChange: null, onValidationChange: null, prefill: {},
};
export default AthenaInsuranceSection;
